<template lang="pug">
  .content-container.column.center
    progress-bar(v-if='contentCompleteness && contentLanguageCompleteness')
    content-header.m-bottom-2(:toggleVideo='toggleVideoModal' v-if='activeContentLanguage')
    .content(v-if='content')
      .row
        .item-1.m-right-2
          content-info
        .item-1.m-left-1
          content-details(:content='content')
      resources(ref='resources')
    loader-toast.loader.absolute(:isSaving='isSaving' :error='savingError')
    video-modal(v-if='showVideoModal' :videoUrl='activeContentLanguage.youtubeLink' @close='toggleVideoModal')
    order-resources-modal(v-if='showOrderResourcesModal')
    krea-file-uploader-modal(v-if='showKreaFileUploaderModal')
    krea-content-image-modal(v-if='showKreaContentImageModal')
    krea-delete-modal(v-if='showKreaDeleteModal' @deletedLanguage='reload')
    krea-publish-content-modal(v-if='showKreaPublishContentModal' @success='goToPublishedList')
    krea-create-in-team-modal(v-if='showCreateInTeamModal')
</template>

<script>
import LoaderToast from '@/components/Krea/LoaderToast'
import ContentHeader from '@/components/Krea/EducationalContent/Header'
import ContentDetails from '@/components/Krea/EducationalContent/Details'
import ContentInfo from '@/components/Krea/EducationalContent/Info'
import Resources from '@/components/Krea/EducationalContent/Resources'
import KreaTextEditor from '@/components/Krea/Inputs/KreaTextEditor'
import OrderResourcesModal from '@/components/Modals/OrderResourcesModal'
import KreaFileUploaderModal from '@/components/Krea/KreaFileUploaderModal'
import KreaDeleteModal from '@/components/Modals/KreaDeleteModal'
import VideoModal from '@/components/EducationalPrograms/VideoModal'
import KreaContentImageModal from '@/components/Krea/EducationalContent/KreaContentImageModal'
import ProgressBar from '@/components/Krea/EducationalContent/ProgressBar/ProgressBar'
import KreaPublishContentModal from '@/components/Modals/KreaPublishContentModal'
import { mapActions, mapState } from 'vuex'
import { innerI18n } from '../main'
import { allLanguages, languagesCodesToString, supportedLanguages } from '../constants/languages'
import kreaContentRedirection from '@/mixins/kreaContentRedirection'
import { routes } from '../router'
import userRole from '../mixins/userRole'
import KreaCreateInTeamModal from '../components/Modals/KreaCreateInTeamModal'

export default {
  mixins: [kreaContentRedirection, userRole],
  components: {
    KreaPublishContentModal,
    KreaCreateInTeamModal,
    KreaFileUploaderModal,
    KreaDeleteModal,
    VideoModal,
    ContentHeader,
    ContentDetails,
    ContentInfo,
    KreaTextEditor,
    OrderResourcesModal,
    KreaContentImageModal,
    ProgressBar,
    Resources,
    LoaderToast
  },
  data() {
    return {
      showVideoModal: false,
      innerI18n
    }
  },
  watch: {
    isSaving(newState) {
      if (!newState) {
        this.reloadCompleteness()
      }
    },
    innerLocale() {
      this.reloadCompleteness()
    }
  },
  computed: {
    ...mapState({
      showKreaPublishContentModal: state => state.behavior.kreaPublishContentModal.show,
      schoolGroupVO: state => state.profile.userProfile.schoolGroupVO,
      userInfo: state => state.userInfo.userInfo,
      content: state => state.kreaEducationalContent.content,
      contentCompleteness: state => state.kreaEducationalContent.contentCompleteness,
      contentLanguageCompleteness: state => state.kreaEducationalContent.contentLanguageCompleteness,
      resources: state => state.kreaEducationalContent.contentResources.resources,
      resourcesTotalCount: state => state.kreaEducationalContent.contentResources.totalCount,
      activeLanguage: state => state.kreaEducationalContent.activeLanguage,
      showOrderResourcesModal: state => state.behavior.orderResourcesModal.show,
      showKreaFileUploaderModal: state => state.behavior.kreaFileUploaderModal.show,
      showKreaDeleteModal: state => state.behavior.kreaDeleteModal.show,
      isSaving: state => state.kreaEducationalContent.isSaving,
      savingError: state => state.kreaEducationalContent.savingError,
      showKreaContentImageModal: state => state.behavior.kreaContentImageModal.show,
      innerLocale: state => state.behavior.innerLocale,
      schools: state => state.userInfo.schoolsWithCourses,
      customFields: state => state.userInfo.customFields,
      showCreateInTeamModal: state => state.behavior.kreaCreateInTeamModal.show
    }),
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.innerLocale}`]
    },
    activeContentLanguage() {
      return this.content
        ? this.content.localizedEducationalPrograms.find(pr => languagesCodesToString[pr.locale] === this.activeLanguage)
        : null
    },
    contentAvailableLanguages() {
      return supportedLanguages
        .filter(lang => Object.keys(this.content.activatedLanguages).includes(lang.lang))
        .map(lang => lang.lang)
    }
  },
  beforeDestroy() {
    this.clearKreaEducationalContent()
  },
  async created() {
    this.showElement({ element: 'fullPageLoader' })
    await this.getUserCustomFields()
    await this.hydrateProfile()
    if (this.isAdmin) await this.hydrateSchoolsWithCourses()
    if (this.customFields) {
      await this.hydrateAcademicCourses({
        schoolId: this.isAdmin
          ? this.schools.find(sch => this.schoolGroupVO.educationalLevels.every(level => sch.educationalLevels.includes(level))).id
          : this.userInfo.schoolId,
        isAdmin: this.isAdmin
      })
    }
    await this.hydrateAges()
    await this.hydrateUdls()
    await this.hydrateKreaEducationalContent(this.$route.params.id)
    const isCustomLanguage = !allLanguages.find(lang => lang.lang === this.activeLanguage).default
    !isCustomLanguage && this.changeInnerLocale(this.activeLanguage)
    await this.hydrateKreaEducationalContentLanguageCompleteness(this.$route.params.id)
    await this.hydrateKreaEducationalContentCompleteness({
      id: this.$route.params.id,
      locale: this.activeLanguage
    })
    if (this.contentAvailableLanguages.includes(this.$route.query.locale)) this.updateLocale(this.$route.query.locale)
    this.hideElement({ element: 'fullPageLoader' })
  },
  methods: {
    ...mapActions([
      'clearKreaEducationalContent',
      'hydrateKreaEducationalContent',
      'hydrateKreaEducationalContentCompleteness',
      'hydrateKreaEducationalContentLanguageCompleteness',
      'setKreaActiveContentLanguage',
      'hydrateSchoolsWithCourses',
      'showElement',
      'hydrateAges',
      'hydrateUdls',
      'hideElement',
      'hydrateProfile',
      'hydrateAcademicCourses',
      'changeInnerLocale',
      'getUserCustomFields'
    ]),
    goToPublishedList() {
      this.hideElement({ element: 'kreaPublishContentModal' })
      this.$router.push({ name: routes.kreaPublished })
    },
    toggleVideoModal() {
      this.showVideoModal = !this.showVideoModal
    },
    updateLocale(locale) {
      this.changeInnerLocale(locale)
      this.setKreaActiveContentLanguage(locale)
    },
    async reload() {
      await this.hydrateKreaEducationalContent(this.$route.params.id)
      await this.hydrateKreaEducationalContentLanguageCompleteness(this.$route.params.id)
      await this.reloadCompleteness()
      await this.$refs.resources.reloadResources()
      this.setKreaActiveContentLanguage(this.innerLocale)
    },
    async reloadCompleteness() {
      await this.hydrateKreaEducationalContentLanguageCompleteness(this.$route.params.id)
      await this.hydrateKreaEducationalContentCompleteness({
        id: this.$route.params.id,
        locale: this.activeLanguage
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/sizes';

.content-container {
  min-height: calc(100vh - #{$footer-height});
  width: 100%;
}

.content {
  max-width: $content-size;
  width: 100%;
  margin: 0;
}
</style>
